import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Policy } from 'src/types/policyTypes';
import { PolicyEngineState, PolicyEngineToast } from '../../types/policyEngineTypes';
import {
	fetchPolicyFeeds,
	fetchConsent,
	createPolicy,
	updatePolicy,
	deletePolicy,
	updatePriorities,
	togglePolicyStatus,
} from './thunks';

const initialState: PolicyEngineState = {
	policyFeeds: [],
	prevFeedsResponse: [],
	loading: false,
	error: null,
	hideConsent: true,
	search: '',
	searchText: '',
	policyChange: false,
	drawerOpen: false,
	selectedPolicyToModify: null,
	modifyClicked: false,
	createPolicyClicked: false,
	toast: null,
};

const policyEngineSlice = createSlice({
	name: 'policyEngine',
	initialState,
	reducers: {
		resetPolicyEngineState: () => initialState,

		setSearch: (state, action: PayloadAction<string>) => {
			state.search = action.payload;
		},

		setSearchText: (state, action: PayloadAction<string>) => {
			state.searchText = action.payload;
		},

		setPolicyChange: (state, action: PayloadAction<boolean>) => {
			state.policyChange = action.payload;
		},

		setDrawerOpen: (state, action: PayloadAction<boolean>) => {
			state.drawerOpen = action.payload;
		},

		setSelectedPolicyToModify: (state, action: PayloadAction<Policy | null>) => {
			state.selectedPolicyToModify = action.payload;
		},

		setModifyClicked: (state, action: PayloadAction<boolean>) => {
			state.modifyClicked = action.payload;
		},

		setCreatePolicyClicked: (state, action: PayloadAction<boolean>) => {
			state.createPolicyClicked = action.payload;
		},

		setPolicyFeeds: (state, action: PayloadAction<Policy[]>) => {
			state.policyFeeds = action.payload;
		},

		reorderPolicyFeeds: (state, action: PayloadAction<{ dragIndex: number; hoverIndex: number }>) => {
			const { dragIndex, hoverIndex } = action.payload;
			const feeds = [...state.policyFeeds];
			[feeds[dragIndex], feeds[hoverIndex]] = [feeds[hoverIndex], feeds[dragIndex]];
			state.policyFeeds = feeds;
		},

		resetPolicyFeedsToPrev: (state) => {
			state.policyFeeds = state.prevFeedsResponse;
		},

		triggerToast: (state, action: PayloadAction<PolicyEngineToast | null>) => {
			state.toast = action.payload;
		},

		clearToast: (state) => {
			state.toast = null;
		},

		openCreatePolicyDrawer: (state) => {
			state.search = '';
			state.searchText = '';
			state.createPolicyClicked = true;
			state.drawerOpen = true;
			state.selectedPolicyToModify = null;
			state.modifyClicked = false;
		},

		openEditPolicyDrawer: (state, action: PayloadAction<Policy>) => {
			state.selectedPolicyToModify = action.payload;
			state.modifyClicked = true;
			state.drawerOpen = true;
		},

		closeDrawer: (state) => {
			state.drawerOpen = false;
			state.selectedPolicyToModify = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch Policy Feeds
			.addCase(fetchPolicyFeeds.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchPolicyFeeds.fulfilled, (state, action: PayloadAction<Policy[]>) => {
				state.loading = false;
				state.policyFeeds = action.payload;
				state.prevFeedsResponse = action.payload;
				state.error = null;
			})
			.addCase(fetchPolicyFeeds.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})

			// Fetch Consent
			.addCase(fetchConsent.fulfilled, (state, action: PayloadAction<boolean>) => {
				state.hideConsent = action.payload;
			})

			// Create Policy
			.addCase(createPolicy.pending, (state) => {
				state.loading = true;
			})
			.addCase(createPolicy.fulfilled, (state, action: PayloadAction<Policy>) => {
				state.loading = false;
				const newPolicy = action.payload;
				const updatedFeeds = state.policyFeeds.map((feed) => ({
					...feed,
					priority: feed.priority + 1,
				}));
				state.policyFeeds = [newPolicy, ...updatedFeeds];
				state.prevFeedsResponse = [newPolicy, ...updatedFeeds];
				state.drawerOpen = false;
				state.selectedPolicyToModify = null;
				state.toast = {
					type: 'success',
					message: `${newPolicy.name} has been created.`,
					duration: 3000,
				};
			})
			.addCase(createPolicy.rejected, (state, action) => {
				state.loading = false;
				state.toast = {
					type: 'error',
					message: action.payload as string,
					duration: 3000,
				};
			})

			// Update Policy
			.addCase(updatePolicy.pending, (state) => {
				state.loading = true;
			})
			.addCase(updatePolicy.fulfilled, (state, action: PayloadAction<Policy>) => {
				state.loading = false;
				const updatedPolicy = action.payload;
				state.policyFeeds = state.policyFeeds.map((feed) =>
					feed.id === updatedPolicy.id ? { ...updatedPolicy } : feed
				);
				state.prevFeedsResponse = state.policyFeeds;
				state.drawerOpen = false;
				state.selectedPolicyToModify = null;
				state.toast = {
					type: 'success',
					message: `Changes has been updated for ${updatedPolicy.name}.`,
					duration: 3000,
				};
			})
			.addCase(updatePolicy.rejected, (state, action) => {
				state.loading = false;
				state.toast = {
					type: 'error',
					message: action.payload as string,
					duration: 3000,
				};
			})

			// Delete Policy
			.addCase(deletePolicy.fulfilled, (state, action) => {
				const { policyId, policyName } = action.payload;
				const updatedFeeds = state.policyFeeds
					.filter((feed) => feed.id !== policyId)
					.map((feed, index) => ({
						...feed,
						priority: index + 1,
					}));
				state.policyFeeds = updatedFeeds;
				state.prevFeedsResponse = updatedFeeds;
				state.toast = {
					type: 'success',
					message: `${policyName} has been deleted.`,
					duration: 3000,
				};
			})
			.addCase(deletePolicy.rejected, (state, action) => {
				state.toast = {
					type: 'error',
					message: action.payload as string,
					duration: 3000,
				};
			})

			// Update Priorities
			.addCase(updatePriorities.fulfilled, (state, action) => {
				state.prevFeedsResponse = state.policyFeeds;
				state.policyChange = false;
				if (!action.payload?.skipToast) {
					state.toast = {
						type: 'success',
						message: 'Priority change has been saved.',
						duration: 3000,
					};
				}
			})
			.addCase(updatePriorities.rejected, (state, action) => {
				state.policyFeeds = state.prevFeedsResponse;
				state.policyChange = false;
				state.toast = {
					type: 'error',
					message: action.payload as string,
					duration: 3000,
				};
			})

			// Toggle Policy Status
			.addCase(togglePolicyStatus.fulfilled, (state, action: PayloadAction<Policy>) => {
				const updatedPolicy = action.payload;
				state.policyFeeds = state.policyFeeds.map((feed) =>
					feed.id === updatedPolicy.id ? { ...updatedPolicy } : feed
				);
				state.toast = {
					type: 'success',
					message: `${updatedPolicy.name} has been ${updatedPolicy.disabled ? 'disabled' : 'enabled'}.`,
					duration: 3000,
				};
			})
			.addCase(togglePolicyStatus.rejected, (state, action) => {
				state.toast = {
					type: 'error',
					message: action.payload as string,
					duration: 3000,
				};
			});
	},
});

export const {
	resetPolicyEngineState,
	setSearch,
	setSearchText,
	setPolicyChange,
	setDrawerOpen,
	setSelectedPolicyToModify,
	setModifyClicked,
	setCreatePolicyClicked,
	setPolicyFeeds,
	reorderPolicyFeeds,
	resetPolicyFeedsToPrev,
	triggerToast,
	clearToast,
	openCreatePolicyDrawer,
	openEditPolicyDrawer,
	closeDrawer,
} = policyEngineSlice.actions;

export default policyEngineSlice.reducer;
